import { type ReactNode, useState } from 'react';
import { i18n } from '@lingui/core';
import { Trans, useLingui } from '@lingui/react/macro';

import { InputCountry } from '@/components/InputCountry/InputCountry';
import { InputLocale } from '@/components/InputLocale/InputLocale';
import Box from '@/design_system/Box';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import { CompanyDetail, useUpdateCompany } from '@/models/company';
import { EditButton, SaveButton } from '@/routes/SettingsBeta/components/SaveEditCancelButtons';
import { SUPPORTED_LOCALES } from '@/services/i18n';
import { getCountryName } from '@/utils/country';

import { InvoiceDataCell } from '../../components/InvoiceDataCell';
import { InvoiceDataForm, normalizeInvoiceDataForSave } from '../../components/InvoiceDataForm';

import { CompanyStripe } from './CompanyStripe';

type InvoiceData = NonNullable<CompanyDetail['invoiceData']>;

const defaultInvoiceData = {
  name: '',
  id: {
    type: 'number',
    value: '',
  },
  address: {
    line1: '',
    zipCode: '',
    city: '',
    country: 'FR',
  },
  shareCapital: {
    amount: 0,
    currency: 'EUR',
  },
  vatNumber: '',
  website: '',
};

export const OverviewSection = ({ company }: { company: CompanyDetail }) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { mutateAsync: updateCompany, isPending: isUpdateCompanyPending } = useUpdateCompany(
    company.id
  );

  const [isEditing, setIsEditing] = useState(false);
  const [country, setCountry] = useState(company.country);
  const [defaultLocale, setDefaultLocale] = useState(company.defaultLocale);
  const [invoiceData, setInvoiceData] = useState<InvoiceData>(
    company.invoiceData ?? defaultInvoiceData
  );
  const [accountingEmail, setAccountingEmail] = useState(company.config?.accounting?.email ?? '');

  const localeLabel = SUPPORTED_LOCALES.find(
    (locale) => locale.id === company.defaultLocale
  )?.label;
  const countryName = company.country ? (getCountryName(company.country) ?? company.country) : '-';

  const resetDraft = () => {
    setCountry(company.country);
    setDefaultLocale(company.defaultLocale);
    setInvoiceData(company.invoiceData ?? defaultInvoiceData);
    setAccountingEmail(company.config?.accounting?.email ?? '');
  };

  const handleEdit = () => {
    resetDraft();
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateCompany({
        country,
        defaultLocale,
        invoiceData: normalizeInvoiceDataForSave(invoiceData),
        config: accountingEmail.trim() ? { accounting: { email: accountingEmail.trim() } } : null,
      });
      setIsEditing(false);
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.invoicing.companies.detail.accounting.save.error">
            Could not save accounting information. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <>
      <Box padding="24px" style={{ marginBottom: '16px', flex: 0 }}>
        <Stack gap="0.75rem">
          <Title as="h2" size="300" weight="medium">
            <Stack row gap="1rem" justifyContent="space-between" alignItems="center">
              <Trans id="settings.organization.invoicing.companies.detail.accounting.title">
                Accounting
              </Trans>
              {isEditing ? (
                <SaveButton
                  label={t({
                    id: 'settings.organization.invoicing.companies.detail.accounting.save',
                    message: 'Save accounting information',
                  })}
                  onClick={handleSave}
                  isLoading={isUpdateCompanyPending}
                />
              ) : (
                <EditButton
                  label={t({
                    id: 'settings.organization.invoicing.companies.detail.accounting.edit',
                    message: 'Edit accounting information',
                  })}
                  onClick={handleEdit}
                />
              )}
            </Stack>
          </Title>

          {isEditing ? (
            <>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.invoice-data',
                  message: 'Invoicing data',
                })}
              >
                <InvoiceDataForm
                  value={invoiceData}
                  onChange={setInvoiceData}
                  defaultCountry={country}
                />
              </FieldRow>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.country',
                  message: 'Country',
                })}
              >
                <InputCountry value={country} onChange={(value) => value && setCountry(value)} />
              </FieldRow>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.default-locale',
                  message: 'Default locale',
                })}
              >
                <InputLocale value={defaultLocale} onChange={setDefaultLocale} />
              </FieldRow>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.accounting-email',
                  message: 'Accounting email',
                })}
              >
                <InputText
                  type="email"
                  value={accountingEmail}
                  onChange={setAccountingEmail}
                  label={t({
                    id: 'settings.organization.invoicing.companies.detail.accounting.accounting-email',
                    message: 'Accounting email',
                  })}
                />
              </FieldRow>
            </>
          ) : (
            <>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.invoice-data',
                  message: 'Invoicing data',
                })}
              >
                {company.invoiceData ? (
                  <InvoiceDataCell invoiceData={company.invoiceData} />
                ) : (
                  <InlineText>{t({ id: '_general.na', message: 'N/A' })}</InlineText>
                )}
              </FieldRow>
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.country',
                  message: 'Country',
                })}
                value={countryName}
              />
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.default-locale',
                  message: 'Default locale',
                })}
                value={localeLabel ? i18n._(localeLabel) : company.defaultLocale}
              />
              <FieldRow
                label={t({
                  id: 'settings.organization.invoicing.companies.detail.accounting.accounting-email',
                  message: 'Accounting email',
                })}
                value={company.config?.accounting?.email}
              />
            </>
          )}
        </Stack>
      </Box>
      {company.stripeAccountStatus !== 'none' && <CompanyStripe company={company} />}
    </>
  );
};

const FieldRow = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string | null;
  children?: ReactNode;
}) => {
  const { t } = useLingui();

  return (
    <Stack row alignItems="flex-start">
      <InlineText weight="medium" style={{ width: '20%' }}>
        {label}
      </InlineText>
      <Stack style={{ width: '80%' }}>
        {children ?? <InlineText>{value ?? t({ id: '_general.na', message: 'N/A' })}</InlineText>}
      </Stack>
    </Stack>
  );
};
